'use client';

import * as React from 'react';
import { Mail, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Typography } from '@/components/ui/typography';
import { ContactForm } from '@/components/frontend/common/contact-form';

interface HeroProps {
  initialTitle?: string;
  initialDescription?: string;
}

export function Hero({ initialTitle, initialDescription }: HeroProps = {}) {
  const t = useTranslations('home.hero');
  const locale = useLocale();
  const [email, setEmail] = React.useState('');
  const [showContactModal, setShowContactModal] = React.useState(false);

  const content = {
    servingPrefix: t('heroServingPrefix'),
    servingHighlight: t('heroServingHighlight'),
    servingSuffix: t('heroServingSuffix'),
    title: t('heroTitle'),
    description: t('heroDescription'),
    emailPlaceholder: t('heroEmailPlaceholder'),
    ctaLabel: t('heroCta'),
    imageAlt: t('heroImageAlt'),
    modalTitle: t('heroModalTitle'),
  };

  // Use server-side data if available, otherwise use translations
  const [heroTitle, setHeroTitle] = React.useState(initialTitle || content.title);
  const [heroDescription, setHeroDescription] = React.useState(initialDescription || content.description);

  // Only fetch if server-side data wasn't provided (for client-side updates)
  React.useEffect(() => {
    // Skip fetch if we already have server-side data
    if (initialTitle && initialDescription) {
      return;
    }

    const fetchHomeHeroTitle = async () => {
      try {
        const res = await fetch(
          `/api/page-titles?page=home&locale=${encodeURIComponent(locale)}`,
          { cache: 'no-store' }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!data) return;

        setHeroTitle(data.title || content.title);
        // Use sectionSubtitle as home hero description (Section content in admin)
        setHeroDescription(data.sectionSubtitle || content.description);
      } catch (error) {

      }
    };

    fetchHomeHeroTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, initialTitle, initialDescription]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setShowContactModal(true);
    }
  };

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-white dark:bg-black pt-20"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.92) 45%, rgba(255,255,255,0.96) 100%)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50/40 via-white/80 to-white dark:from-black dark:via-gray-900 dark:to-black pointer-events-none" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200/25 dark:bg-purple-900/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-200/25 dark:bg-blue-900/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808014_1px,transparent_1px),linear-gradient(to_bottom,#80808014_1px,transparent_1px)] bg-[size:22px_22px] opacity-90 dark:bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] dark:opacity-100 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_25%,rgba(59,130,246,0.08),transparent_35%),radial-gradient(circle_at_75%_40%,rgba(99,102,241,0.08),transparent_40%)] dark:hidden pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10 flex-1 flex flex-col">
        <div className="max-w-4xl mx-auto text-center mb-16">

          <Typography variant="p" className="mb-4">
            {content.servingPrefix}
            <span className="font-semibold text-primary-600 dark:text-primary-400  px-1.5 py-0.5 rounded">
              {content.servingHighlight}
            </span>
            {content.servingSuffix}
          </Typography>
          <Typography variant="h1" className="mb-6">
            {(() => {
              // Split title to find and animate "#1 AI-Driven" (case-insensitive)
              const title = heroTitle;
              const searchText = '#1 AI-Driven';
              const lowerTitle = title.toLowerCase();
              const lowerSearch = searchText.toLowerCase();
              const index = lowerTitle.indexOf(lowerSearch);

              if (index === -1) {
                // If "#1 AI-Driven" not found, return title as is
                return title;
              }

              const before = title.substring(0, index);
              const animatedText = title.substring(index, index + searchText.length);
              const after = title.substring(index + searchText.length);

              return (
                <>
                  {before}
                  <span className="inline-block relative mx-1">
                    <span className="relative z-10 bg-gradient-to-r from-primary-600 via-purple-600 to-blue-600 dark:from-primary-400 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent animate-gradient-x bg-[length:200%_auto] font-bold">
                      {animatedText}
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-purple-600/20 to-blue-600/20 dark:from-primary-400/20 dark:via-purple-400/20 dark:to-blue-400/20 blur-xl animate-pulse"></span>
                  </span>
                  {after}
                </>
              );
            })()}
          </Typography>

          <Typography variant="p" className="mb-10 max-w-4xl mx-auto text-lg">
            {heroDescription || content.description}
          </Typography>

          <form onSubmit={handleSubmit} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <div className="flex-1 relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={content.emailPlaceholder}
                  className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                className="px-8 py-4 border-2 border-indigo-600 dark:border-indigo-600 bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-primary-700 dark:hover:bg-primary-700 hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 font-semibold rounded-lg transition-all duration-300"
              >
                {content.ctaLabel}
              </button>
            </div>
          </form>

        </div>

        <div className="relative w-full max-w-6xl mx-auto mt-auto">
          {/* Native picture element: browser downloads ONLY the matching source.
              Avoids preloading the 100KB desktop asset on mobile. */}
          <picture>
            <source
              media="(min-width: 768px)"
              srcSet="/images/home/best-mlm-software.webp"
            />
            <img
              src="/images/home/best-mlm-software-usa.webp"
              alt="Business MLM Software Dashboard - AI-Powered Network Marketing Platform for Direct Selling Companies"
              width={1200}
              height={800}
              className="w-full h-auto object-contain"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
          onClick={() => {
            setShowContactModal(false);
            setEmail(''); // Reset email field when closing modal
          }}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{content.modalTitle}</h2>
              <button
                onClick={() => {
                  setShowContactModal(false);
                  setEmail(''); // Reset email field when closing modal
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-6">
              <ContactForm
                source="contact"
                initialEmail={email}
                onClose={() => {
                  setShowContactModal(false);
                  setEmail(''); // Reset email field in hero section after successful submission
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

